import { documentName, fill, t, type SnapshotLine } from '@bms2/shared';
import { RichTextView } from '../../components/controls';
import { Money } from '../../components/Money';
import { Modal } from '../../components/modals/Modal';
import { useApi } from './api';
import { TermsAsSentLine, TermsClauseList, documentCopyPath, type DocumentTermsClause } from './DocumentTermsModal';
import styles from './DocumentTermsModal.module.css';
import { useDayText } from '../../shell/viewerDates';

/** The server's answer for one frozen copy, typed against what the named-copy route sends (T277; decision 0773). */
export interface DocumentCopyAnswer {
  documentType: string;
  documentId: string;
  versionNumber: number;
  heading: string;
  sentAt: string;
  sentByName: string;
  currencyCode: string;
  totalCents: number;
  lines: SnapshotLine[];
  clauses: DocumentTermsClause[];
  termsOverride: string | null;
  predatesKeptTerms: boolean;
}

/** One line of a copy as the window lists it: its number, its words, and how many of what. */
function lineText(line: SnapshotLine): string {
  return `${line.lineNumber}. ${line.description} · ${line.quantity} ${line.unitName}`;
}

export const documentCopyModalDocumentedStates = ['loading', 'copy', 'predates'] as const;

/**
 * 🔒 One frozen copy, opened from its entry in the version list (T277; decision
 * 0773): what the document went out as, who sent it and when, its lines, and the
 * terms it carried, read-only. 🔨 Mounted only while open and keyed by the copy it
 * names, so a second entry opens its own copy and never the first one's.
 */
export function DocumentCopyModal({ documentType, documentId, versionNumber, apiBaseUrl = '', onClose }: { documentType: string; documentId: string; versionNumber: number; apiBaseUrl?: string; onClose: () => void }) {
  const dayText = useDayText();
  const { state } = useApi<DocumentCopyAnswer>(documentCopyPath(documentType, documentId, versionNumber), apiBaseUrl);
  const copy = state.kind === 'ready' ? state.data : null;
  return (
    <Modal open width="wide" title={copy ? copy.heading : t('document.copy.title')} subtitle={documentName(documentType)} onCancel={onClose} dataModal="document-copy" actions={[
      // 🔒 Decision 2140: the copy as sent is read, never written, so it carries one Close (decision 3063).
      { kind: 'close', label: t('modal.close'), onAct: onClose },
    ]}>
      {copy === null ? (
        <p className={styles.muted}>{state.kind === 'loading' ? t('document.terms.loading') : t('document.copy.unavailable')}</p>
      ) : (
        <div className={styles.body} data-document-copy={copy.versionNumber}>
          <p className={styles.muted} data-document-copy-sent="true">{fill(t('document.copy.sent'), { date: dayText(copy.sentAt), name: copy.sentByName })}</p>
          <h3 className={styles.heading}>{t('document.copy.lines')}</h3>
          <ul className={styles.copyLines} data-document-copy-lines={copy.lines.length}>
            {copy.lines.map((line) => (
              <li key={line.lineNumber} className={styles.copyLine}>
                <span>{lineText(line)}</span>
                <Money cents={line.amountCents} currencyCode={copy.currencyCode} />
              </li>
            ))}
          </ul>
          <p className={styles.copyTotal}><span>{t('document.copy.total')}</span><Money cents={copy.totalCents} currencyCode={copy.currencyCode} /></p>
          <h3 className={styles.heading}>{t('document.copy.terms')}</h3>
          <TermsAsSentLine sentAt={copy.sentAt} predates={copy.predatesKeptTerms} />
          {copy.termsOverride !== null ? (
            <>
              <div className={styles.own} data-document-terms-own="true"><RichTextView html={copy.termsOverride} /></div>
              {/* 🔒 The clauses a confidentiality role added printed after the own text (decision 1171). */}
              {copy.clauses.length > 0 ? <TermsClauseList clauses={copy.clauses} /> : null}
            </>
          ) : copy.clauses.length === 0 ? (
            <p className={styles.muted}>{t('document.terms.none')}</p>
          ) : (
            <TermsClauseList clauses={copy.clauses} />
          )}
        </div>
      )}
    </Modal>
  );
}
